import React, {useState, useEffect, useRef} from "react"
import CondominioCard from "@/components/condos/CondoCard"
import {CSVPopUpWindow} from "@/components/csv/CSVPopUpWindow"
import {AxiosResponse} from "axios"
import {inmAndCondStyles, CDBLUE} from "@/constants"
import ListAltIcon from "@mui/icons-material/ListAlt"
import {getCondos} from "@/api/condo"
import AddIcon from "@mui/icons-material/Add"
import Page from "@/layouts/Page"
import {Link, useLocation} from "react-router-dom"
import CloseIcon from "@mui/icons-material/Close"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import {useAuthContext} from "@/hooks/useAuthContext"
import {useRegion} from "@/context/RegionContext"
import RegionSelector from "@/components/ui/RegionSelector"
import {condoClasses} from "@casas-diamante/types"
import {Box} from "@mui/material"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import Select, {SelectChangeEvent} from "@mui/material/Select"
import Chip from "@mui/material/Chip"
import OutlinedInput from "@mui/material/OutlinedInput"
import {
   CondoSearchState,
   persistCondoSearchState,
   readPersistedCondoSearchState,
   useCondoSearchState,
} from "@/hooks/useCondoBackNavigation"

function CondosFeed() {
   const location = useLocation()
   const restoredRef = useRef(false)
   const pendingRestoreRef = useRef<CondoSearchState | null>(null)
   const [condominios, setCondominios] = useState<any[]>([])
   const [lastCond, setLastCond] = useState<null | string>(null)
   const [loading, setLoading] = useState<boolean>(false)
   const [noMoreCond, setNoMoreCond] = useState(false)
   const [searchString, setSearchString] = useState("")
   const [searchPage, setSearchPage] = useState(0)
   const [visibility, setVisibility] = useState(false)
   const [classification, setClassification] = useState<string | null>(null)
   const {user} = useAuthContext()
   const {selectedRegion} = useRegion()

   // Local storage logic to preserve some state
   const storedSearchQuery = localStorage.getItem("condosQuery") || ""
   const setStoredSearchQuery = (val: string) => localStorage.setItem("condosQuery", val)

   const storedClassification = localStorage.getItem("condosClassification") || ""
   const setStoredClassification = (val: string) =>
      localStorage.setItem("condosClassification", val)

   const fetchedCondos = JSON.parse(localStorage.getItem("condos") || "[]")
   const setFetchedCondos = (val: any[]) => localStorage.setItem("condos", JSON.stringify(val))

   useEffect(() => {
      const restoredSearch = (
         location.state as {restoredSearch?: CondoSearchState} | null
      )?.restoredSearch ?? readPersistedCondoSearchState()

      if (!restoredSearch || restoredRef.current || restoredSearch.source !== "condos") {
         return
      }

      restoredRef.current = true
      setSearchString(restoredSearch.searchString ?? "")
      setSearchPage(restoredSearch.searchPage ?? 0)
      setCondominios(restoredSearch.condos)
      setLastCond(restoredSearch.lastCond ?? null)
      setNoMoreCond(restoredSearch.noMoreCond ?? false)
      setClassification(restoredSearch.classification ?? null)
      setFetchedCondos(restoredSearch.condos)
      setStoredSearchQuery(restoredSearch.searchString ?? "")
      setStoredClassification(restoredSearch.classification ?? "")
      setLoading(false)
      pendingRestoreRef.current = restoredSearch
   }, [location.state])

   // run this when the component mounts
   useEffect(() => {
      setNoMoreCond(false)
      const fetchCondos = async () => {
         setLoading(true)
         if (restoredRef.current) {
            setLoading(false)
            return
         }
         // fetch first condos
         if (storedSearchQuery) setSearchString(storedSearchQuery)
         if (storedClassification) setClassification(storedClassification)
         if (fetchedCondos?.length > 0) {
            setLoading(false)
            setCondominios(fetchedCondos)
            return
         }
         const res = await getCondos(
            selectedRegion,
            null,
            storedSearchQuery ? storedSearchQuery : searchString,
            searchPage,
            storedClassification || "",
         )
         await changeState(res)
      }
      fetchCondos()
   }, [])

   useEffect(() => {
      const restoredSearch = pendingRestoreRef.current

      if (!restoredSearch || condominios.length === 0) return

      const restorePosition = () => {
         const selectedCard = restoredSearch.selectedCondoId
            ? document.querySelector(`[data-condo-id="${restoredSearch.selectedCondoId}"]`)
            : null

         if (selectedCard instanceof HTMLElement) {
            selectedCard.scrollIntoView({behavior: "auto", block: "center"})
         } else {
            window.scrollTo({top: restoredSearch.scrollY, behavior: "auto"})
         }

         pendingRestoreRef.current = null
      }

      const timeoutId = window.setTimeout(() => {
         requestAnimationFrame(restorePosition)
      }, 0)

      return () => {
         window.clearTimeout(timeoutId)
      }
   }, [condominios.length])

   // helper functions to change state and condominios
   const changeState = async (res: AxiosResponse<any, any>) => {
      // if nothing is found, return without changing any state
      if (res.data.data.length === 0) {
         setLoading(false)
         setCondominios([])
         setFetchedCondos([])
         setNoMoreCond(true)
         return
      }
      // set condominios to the data we get from node
      setCondominios(res.data.data)
      setFetchedCondos(res.data.data)
      // asign the last condominio to the last element of our response
      setLastCond(res.data.data[res.data.data.length - 1].name)
      setLoading(false)
   }

   const changeCondominios = async (res: AxiosResponse<any, any>) => {
      // when more data is fetched, we just concatenate the previous condominios
      // with the new ones
      if (res.data.data.length === 0) {
         setNoMoreCond(true)
         return
      }
      setCondominios([...condominios, ...res.data.data])
      setFetchedCondos([...condominios, ...res.data.data])
      // asign the last condominio to the last element of our response
      setLastCond(res.data.data[res.data.data.length - 1].name)
      setLoading(false)
   }

   const condoSearchState = useCondoSearchState({
      source: "condos",
      returnTo: "/condominios",
      condos: condominios,
      searchString,
      searchPage,
      lastCond,
      noMoreCond,
      classification,
   })

   useEffect(() => {
      if (condominios.length === 0 && !searchString) return

      persistCondoSearchState({
         ...condoSearchState,
         scrollY: window.scrollY,
      })
   }, [condoSearchState, condominios.length, searchString])

   useEffect(() => {
      if (condominios.length === 0 && !searchString) return

      const saveScrollPosition = () => {
         persistCondoSearchState({
            ...condoSearchState,
            scrollY: window.scrollY,
         })
      }

      window.addEventListener("scroll", saveScrollPosition, {passive: true})

      return () => {
         window.removeEventListener("scroll", saveScrollPosition)
      }
   }, [condoSearchState, condominios.length, searchString])

   const handleClassificationChange = (event: SelectChangeEvent<string>) => {
      const value = event.target.value
      const newValue = value === classification ? null : value
      setClassification(newValue)
      setStoredClassification(newValue ?? "")
   }

   const handleSearchStringChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchString(e.target.value)
      setStoredSearchQuery(e.target.value)
      if (e.target.value === "") {
         setSearchPage(0)
         setNoMoreCond(false)
         setCondominios([])
         setFetchedCondos([])
         setLoading(true)
         const fetchCondos = async () => {
            const res = await getCondos(selectedRegion, null, "", 0, classification ?? "")
            await changeState(res)
         }
         fetchCondos()
      }
   }

   const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setLoading(true)
      setNoMoreCond(false)
      const res = await getCondos(selectedRegion, null, searchString, searchPage, classification ?? "")
      await changeState(res)
   }

   const fetchMore = async () => {
      const res = await getCondos(selectedRegion, lastCond, searchString, searchPage + 1, classification ?? "")
      if (searchString) {
         setSearchPage(searchPage + 1)
      }
      await changeCondominios(res)
   }

   const clearSearchString = () => {
      setSearchString("")
      setStoredSearchQuery("")
      setClassification(null)
      setStoredClassification("")
      setSearchPage(0)
      setNoMoreCond(false)
      setCondominios([])
      setFetchedCondos([])
      setLoading(true)
      const fetchCondos = async () => {
         const res = await getCondos(selectedRegion, null, "", 0)
         await changeState(res)
      }
      fetchCondos()
   }

   return (
      <Page className="flex flex-col items-center my-8 gap-4 p-4 justify-center">
         <h1 className="text-2xl md:text-3xl font-bold text-blue text-center">
            Buscador de Condominios Socios
         </h1>
         <section className="flex flex-col md:flex-row gap-4 md:gap-8">
            {user.admin && (
               <>
                  <Link
                     to="/condominios/nuevo"
                     className="flex py-2 px-4 gap-2 rounded-full bg-blue text-white"
                  >
                     <AddIcon />
                     Agregar Condominio
                  </Link>
                  <button
                     onClick={() => setVisibility(true)}
                     className="flex py-2 px-4 gap-2 rounded-full bg-blue text-white"
                  >
                     Ver modo Excel
                     <ListAltIcon />
                  </button>
               </>
            )}
         </section>
         <RegionSelector />
         <FormControl className="w-[250px]" color="primary" size="small">
            <InputLabel>Clasificación</InputLabel>
            <Select
               value={classification || ""}
               onChange={handleClassificationChange}
               input={<OutlinedInput label="Clasificación" />}
               renderValue={(selected) => (
                  <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                     {selected && (
                        <Chip key={selected} label={selected} sx={{bgcolor: CDBLUE, color: "white"}} />
                     )}
                  </Box>
               )}
            >
               {condoClasses.map((c) => (
                  <MenuItem
                     key={c}
                     value={c}
                     onClick={() => setClassification((prev) => (prev === c ? null : c))}
                     selected={classification === c}
                  >
                     {c}
                  </MenuItem>
               ))}
            </Select>
         </FormControl>
         <form
            action="submit"
            onSubmit={handleSearch}
            className="flex flex-col items-center content-center gap-4"
         >
            <div className="flex border border-blue rounded-md w-full px-2 py-1 items-center">
               <input
                  type="text"
                  name="Search String"
                  id="search-string"
                  value={searchString}
                  onChange={handleSearchStringChange}
                  className="px-2 py-1 focus:outline-none"
                  placeholder="Buscar por nombre"
               />
               <button onClick={clearSearchString} type="button">
                  <CloseIcon />
               </button>
            </div>
            <button className="border rounded-[10px] bg-blue text-white px-2 py-1" type="submit">
               Buscar
            </button>
         </form>
         {loading ? (
            <h2 className={inmAndCondStyles.loading}>Cargando...</h2>
         ) : (
            <section className="flex flex-col gap-4 w-full items-center">
               {condominios.map((condominio) => (
                  <CondominioCard
                     key={condominio.id}
                     condo={condominio}
                     searchState={condoSearchState}
                  />
               ))}
            </section>
         )}
         <section className="my-8 flex flex-col gap-4 ">
            {!noMoreCond && condominios.length % 20 === 0 && (
               <button
                  onClick={fetchMore}
                  className="border rounded-[10px] bg-green text-white px-2 py-1 mx-auto"
               >
                  Cargar más
               </button>
            )}
            <button
               onClick={() => window.scrollTo({top: 0, behavior: "smooth"})}
               className="border rounded-[10px] bg-green text-white px-2 py-1 flex items-center gap-2 mx-auto"
            >
               Volver arriba
               <ArrowUpwardIcon />
            </button>
         </section>
         {visibility && <CSVPopUpWindow setVisibility={setVisibility} />}
      </Page>
   )
}

export default CondosFeed
